import { State as DataState } from '@progress/kendo-data-query';
import {
  GridCellProps,
  GridDataStateChangeEvent,
} from '@progress/kendo-react-grid';
import { produce } from 'immer';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { formatDate } from '../../../../../../common/global/helpers';
import Breadcrumbs from '../../../../../../common/widgets/Breadcrumbs';
import Dropdown from '../../../../../../common/widgets/Dropdown';
import Grid, { GridCellTd } from '../../../../../../common/widgets/Grid';
import Icons from '../../../../../../common/widgets/Icons';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import { config } from '../../../../config';
import { CardType } from '../../../../enums';
import { EmployeeCardTransactionItemModel } from '../../../../models/Cards';
import { DropdownOptionModel } from '../../../../models/Common';
import { resources } from '../../../../resources/resources';
import { cardService } from '../../../../services/card';
import applicationCrumbs from '../../../ApplicationCrumbs';
import { EmptyStateMyCardNoTranscations } from '../../../Common/EmptyStates';
import PlatformInformation from '../../../Common/PlatformInformation';
import MyAccountMenu from '../../Common/MyAccountMenu';

interface FiltersData {
  period: DropdownOptionModel;
}

interface TransactionHistoryPageState {
  transactions?: EmployeeCardTransactionItemModel[];
  filterState: FiltersData;
  dataState: DataState;
  showErrorPanel?: boolean;
}

const getTransactionIcon = (cardType: CardType) => {
  if (cardType === CardType.Meal) return <Icons.CardTransactionMeal />;
  if (cardType === CardType.Vacation) return <Icons.CardTransactionVacation />;
  if (cardType === CardType.Culture) return <Icons.CardTransactionCultural />;
  if (cardType === CardType.Gift) return <Icons.CardTransactionGift />;
};

export const TransactionHistoryPage = () => {
  const { t: translate } = useTranslation();
  const crumbs = applicationCrumbs(translate);
  const params = useParams();
  const navigate = useNavigate();
  const requestHistoryRef = useRef<Set<string>>(new Set());

  const periodOptions: DropdownOptionModel[] = useMemo(
    () => [
      {
        text: translate(resources.fromLastNDays, { 0: 30 }),
        value: new Date(moment().add({ days: -30 }).toString()),
      },
      {
        text: translate(resources.fromLastNDays, { 0: 90 }),
        value: new Date(moment().add({ days: -90 }).toString()),
      },
      {
        text: translate(resources.fromLastNMonths, { 0: 6 }),
        value: new Date(moment().add({ months: -6 }).toString()),
      },
    ],
    [translate],
  );

  const initialFiltersState = useMemo(
    () => ({
      period: periodOptions[0],
    }),
    [periodOptions],
  );

  const [state, setState] = useState<TransactionHistoryPageState>({
    filterState: initialFiltersState,
    dataState: {
      skip: 0,
      take: config.grid.defaultPageSize,
    },
  });
  const [transactionsToDisplay, setTransactionsToDisplay] =
    useState<EmployeeCardTransactionItemModel[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const dataStateChange = (e: GridDataStateChangeEvent) => {
    setState(
      produce(draft => {
        draft.dataState.take = e.dataState.take;
        draft.dataState.skip = e.dataState.skip;
      }),
    );
  };

  const onPeriodChange = (period: DropdownOptionModel) => {
    setState(
      produce(draft => {
        draft.filterState.period = period;
      }),
    );
  };

  useEffect(() => {
    requestHistoryRef.current.clear();
    setState(
      produce(draft => {
        draft.dataState.skip = 0;
        draft.transactions = [];
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filterState.period.value, state.dataState.take]);

  useEffect(() => {
    const loadTransactions = async () => {
      if (!params?.cardId) return;

      const pageSize =
        state.dataState.skip ?? 0 > 0
          ? state.dataState.take ?? config.grid.defaultPageSize
          : (state.dataState.take ?? config.grid.defaultPageSize) * 2;
      const pageNumber =
        state.dataState.skip ?? 0 > 0
          ? (state.dataState.skip ?? 0) /
              (state.dataState.take ?? config.grid.defaultPageSize) +
            2
          : 1;

      if (!requestHistoryRef.current?.has(`${pageSize}-${pageNumber}`)) {
        requestHistoryRef.current?.add(`${pageSize}-${pageNumber}`);
        const cardId = Number.parseInt(params.cardId);
        setIsLoading(() => true);

        const response = await cardService.getCardTransactions(
          cardId,
          state.filterState.period.value,
          pageSize,
          pageNumber,
        );

        if (!response.hasErrors) {
          setState(
            produce(draft => {
              if (!draft.transactions) {
                draft.transactions = response.data;
              } else {
                draft.transactions.push(...response.data);
              }
            }),
          );
        } else {
          requestHistoryRef.current?.delete(`${pageSize}-${pageNumber}`);
          setState(
            produce(d => {
              d.showErrorPanel = true;
            }),
          );
        }

        setIsLoading(() => false);
      }
    };

    loadTransactions().catch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params.cardId,
    state.filterState.period.value,
    state.dataState.take,
    state.dataState.skip,
  ]);

  useEffect(() => {
    if (state.transactions) {
      const start: number = state.dataState.skip ?? 0;
      const end: number = Math.min(
        state.transactions?.length,
        (state.dataState.skip ?? 0) +
          (state.dataState.take ?? config.grid.defaultPageSize),
      );

      setTransactionsToDisplay(state.transactions?.slice(start, end));
    }
  }, [state.transactions, state.dataState.skip, state.dataState.take]);

  return (
    <>
      <Breadcrumbs
        entries={[
          crumbs.home,
          crumbs.accountDashboard,
          crumbs.cards,
          crumbs.transactions,
        ]}
      />
      <section className="section-main section-actions my-account-page transaction-history-page">
        <div className="container">
          <div className="area-actions">
            <MyAccountMenu />
          </div>
          <div className="area-content">
            <PlatformInformation />
            <div className="panel padding-xl">
              <div className="btn-panel-back-wrapper">
                <a onClick={() => navigate(-1)} className="btn btn-panel-back">
                  <span className="btn-text">{translate(resources.cards)}</span>
                </a>
              </div>

              <div className="page-actions">
                <h1>{translate(resources.employeeCards.transactions.title)}</h1>
                <div className="page-action card-history-selector">
                  <label>
                    {translate(resources.employeeCards.choosePeriod)}:
                  </label>
                  <Dropdown
                    data={periodOptions ?? []}
                    value={state?.filterState.period}
                    onChange={e => onPeriodChange?.(e.value)}
                    dataItemKey="value"
                    textField="text"
                  />
                </div>
              </div>

              <Loader
                loading={!state.transactions || isLoading}
                loader={<LoadingSpinner />}
                showErrorPanel={state.showErrorPanel}
              >
                {state?.transactions?.length ? (
                  <div className="my-account-grid card-history-grid">
                    <Grid
                      data={transactionsToDisplay ?? []}
                      pageable={{
                        pageSizes: config.grid.pageSizes,
                        pageSizeValue: state.dataState.take,
                        info: false,
                        previousNext: true,
                      }}
                      {...state.dataState}
                      total={state.transactions.length}
                      onDataStateChange={dataStateChange}
                      columns={[
                        {
                          field: 'commerciantName',
                          title: translate(
                            resources.employeeCards.transactions
                              .commerciantName,
                          ),
                          cell: ({ dataItem }: GridCellProps) => {
                            return (
                              <GridCellTd className="td-grid-allign">
                                {getTransactionIcon(dataItem.cardType)}{' '}
                                <span>{dataItem.commerciantName}</span>
                              </GridCellTd>
                            );
                          },
                        },
                        {
                          field: 'transactionType',
                          title: translate(
                            resources.employeeCards.transactions
                              .transactionType,
                          ),
                          cell: TransactionTypeCell,
                        },
                        {
                          field: 'creationDate',
                          title: translate(
                            resources.employeeCards.transactions.date,
                          ),
                          cell: DateCell,
                        },
                        {
                          field: 'amount',
                          title: translate(
                            resources.employeeCards.transactions.total,
                          ),
                          cell: AmountCell,
                        },
                      ]}
                    />
                  </div>
                ) : (
                  <div>
                    <EmptyStateMyCardNoTranscations />
                  </div>
                )}
              </Loader>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const TransactionTypeCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>{dataItem.transactionType}</GridCellTd>
);

const DateCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>{formatDate(dataItem.creationDate)}</GridCellTd>
);

const AmountCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>
    {dataItem.amount} {config.currency}
  </GridCellTd>
);
